import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { useI18n } from "@expertloop/i18n/react";
import { useAuthStore } from "./stores/authStore";
import { LoginPage } from "./pages/LoginPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { DashboardLayout } from "./components/DashboardLayout";
import { OverviewPage } from "./pages/OverviewPage";
import { FinancePage } from "./pages/FinancePage";
import { ExpertReferralsPage } from "./pages/ExpertReferralsPage";
import { ChatsPage } from "./pages/ChatsPage";
import { ChatDetailPage } from "./pages/ChatDetailPage";
import { UsersPage } from "./pages/UsersPage";
import { UserDetailPage } from "./pages/UserDetailPage";
import { GuestDetailPage } from "./pages/GuestDetailPage";
import { AuditLogPage } from "./pages/AuditLogPage";
import { AdminsPage } from "./pages/AdminsPage";
import { SecuritySettingsPage } from "./pages/SecuritySettingsPage";
import { ReportsPage } from "./pages/ReportsPage";
import { TranslationsPage } from "./pages/TranslationsPage";
import { TranslationDetailPage } from "./pages/TranslationDetailPage";

function ProtectedRoute() {
	const { t } = useI18n();
	const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
	const isLoading = useAuthStore((s) => s.isLoading);

	if (isLoading) {
		return <div className="loading">{t("common.loading")}</div>;
	}

	if (!isAuthenticated) {
		return <Navigate to="/login" replace />;
	}

	return <Outlet />;
}

function PublicRoute() {
	const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

	if (isAuthenticated) {
		return <Navigate to="/" replace />;
	}

	return <Outlet />;
}

// Le basename suit la config Vite (`base`) : "/admin/" en dev local (proxy),
// "/" en production où l'app est servie à la racine du sous-domaine admin.
// BASE_URL a un slash final que React Router n'accepte pas → on le retire.
const basename = import.meta.env.BASE_URL.replace(/\/$/, "");

export const router = createBrowserRouter([
	{
		element: <PublicRoute />,
		children: [
			{
				path: "/login",
				element: <LoginPage />,
			},
		],
	},
	// Hors de PublicRoute : un administrateur encore connecté dans ce navigateur
	// doit pouvoir ouvrir le lien reçu par email, pas être renvoyé au tableau de bord
	{
		path: "/forgot-password",
		element: <ForgotPasswordPage />,
	},
	{
		path: "/reset-password",
		element: <ResetPasswordPage />,
	},
	{
		element: <ProtectedRoute />,
		children: [
			{
				element: <DashboardLayout />,
				children: [
					{
						path: "/",
						element: <OverviewPage />,
					},
					{
						path: "/finance",
						element: <FinancePage />,
					},
					{
						// Parrainage entre experts (lot E6)
						path: "/expert-referrals",
						element: <ExpertReferralsPage />,
					},
					{
						path: "/chats",
						element: <ChatsPage />,
					},
					{
						path: "/chats/:chatId",
						element: <ChatDetailPage />,
					},
					{
						path: "/reports",
						element: <ReportsPage />,
					},
					{
						path: "/translations",
						element: <TranslationsPage />,
					},
					{
						path: "/translations/:locale",
						element: <TranslationDetailPage />,
					},
					{
						path: "/users",
						element: <UsersPage />,
					},
					{
						path: "/users/:userId",
						element: <UserDetailPage />,
					},
					{
						path: "/guests/:email",
						element: <GuestDetailPage />,
					},
					{
						path: "/audit",
						element: <AuditLogPage />,
					},
					{
						path: "/admins",
						element: <AdminsPage />,
					},
					{
						path: "/security",
						element: <SecuritySettingsPage />,
					},
				],
			},
		],
	},
], { basename });
